/**
 * CompletionScene.ts — Post-game summary after reaching an ending.
 *
 * Shows ending epilogue, post-game statistics, trait summary,
 * relationship summary, and records completion in vault.
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import { getClass } from '@game/data/classes';
import { getNPC } from '@game/data/npcs';
import { TitleScene } from './TitleScene';
import { CharCreateScene } from './CharCreateScene';
import { formatPlaytime } from './CharacterScene';
import { analytics } from '@game/Analytics';

export class CompletionScene implements Scene {
  name = 'CompletionScene';

  async enter(engine: GameEngine): Promise<void> {
    const ctx = engine.getContext();
    const { effects, menu, gameState, storyEngine, saveSystem, characterSystem, settings, frameRenderer, visualLayer } = ctx;

    if (!gameState) {
      await engine.replaceScene(new TitleScene());
      return;
    }

    const cls = getClass(gameState.character.classId);
    const speed = settings.getTypewriterSpeed();

    // Hide status bar for the ending
    ctx.statusBar.hide();

    // Set ending-specific particles
    visualLayer.setLocationParticles('gallowcrest_citadel');

    // ─── Ending Epilogue ─────────────────────────────

    ctx.sprites.hide();
    const epilogueLayout = frameRenderer.drawFrame({
      title: 'THE END',
      borderColor: 'runeGold',
      headerColor: 'runeGold',
    });

    const epilogueWriter = frameRenderer.createWriter(epilogueLayout.content, effects);
    epilogueWriter.blankLine();
    epilogueWriter.blankLine();

    const finalNode = storyEngine.getCurrentNode();

    // Render epilogue using PanelWriter (frame-aware)
    const epilogueText = finalNode?.description
      ?? 'Your journey through the Greyveil has reached its end. The choices you made have shaped the fate of the frozen north.';
    const epilogueLines = epilogueText.split(/\n\n?/).filter((l: string) => l.trim().length > 0);
    if (speed > 0) {
      await epilogueWriter.typewriterParagraph(epilogueLines, 'white', speed);
    } else {
      for (const line of epilogueLines) {
        epilogueWriter.writeLine(line, 'white');
      }
    }

    await epilogueWriter.pressAnyKey();

    // ─── Post-Game Statistics ────────────────────────

    ctx.sprites.hide();
    const statsLayout = frameRenderer.drawFrame({
      title: 'JOURNEY COMPLETE',
      headerRight: `${gameState.character.name} \u2014 ${cls.name}`,
      borderColor: 'runeGold',
    });

    const statsWriter = frameRenderer.createWriter(statsLayout.content, effects);
    statsWriter.blankLine();

    // Character summary
    statsWriter.writeLine(`  ${gameState.character.name} \u2014 ${cls.name}`, 'icyWhite');
    statsWriter.writeLine(`  Level ${gameState.character.level}`, 'frostSilver');
    statsWriter.blankLine();

    // Playtime
    statsWriter.writeLine(`  Playtime: ${formatPlaytime(gameState.playtime)}`, 'white');

    // Nodes visited
    const visitedCount = gameState.story.visitedNodes.size;
    statsWriter.writeLine(`  Nodes visited: ${visitedCount}`, 'white');

    // Choices made
    statsWriter.writeLine(`  Choices made: ${gameState.story.choiceHistory.length}`, 'white');

    statsWriter.blankLine();

    // ─── Trait Summary ───────────────────────────────

    if (gameState.traits.size > 0) {
      statsWriter.writeLine('  Traits:', 'yellow');
      for (const [, trait] of gameState.traits) {
        statsWriter.writeLine(`    ${trait.name} (Lv.${trait.level})`, 'frostBlue');
      }
      statsWriter.blankLine();
    }

    // ─── Relationship Summary ────────────────────────

    if (gameState.relationships.size > 0) {
      statsWriter.writeLine('  Final Relationships:', 'yellow');
      for (const [npcId, score] of gameState.relationships) {
        const tier = characterSystem.getRelationshipTier(score);
        const npc = getNPC(npcId);
        const name = npc?.name ?? npcId;
        statsWriter.writeLine(`    ${name}: ${tier} (${score})`, 'white');
      }
      statsWriter.blankLine();
    }

    // Viewport break before end menu to prevent overflow
    await statsWriter.pressAnyKey();

    // ─── Record Completion in Vault ──────────────────

    analytics.endingReached(
      finalNode?.id ?? 'unknown',
      cls.name,
      gameState.playtime,
      gameState.character.level,
    );

    const vault = saveSystem.loadVault();
    vault.completions.push({
      classId: gameState.character.classId,
      endingId: finalNode?.id ?? 'unknown',
      completedAt: new Date().toISOString(),
      playtime: gameState.playtime,
      level: gameState.character.level,
    });
    vault.totalPlaytime += gameState.playtime;

    // Check completion-only achievements now that the game is actually complete
    ctx.achievementSystem.checkAll(gameState, vault, 'completion');

    saveSystem.saveVault(vault);

    // ─── Subscribe Prompt (one-time) ───────────────────────

    const SUBSCRIBE_KEY = 'choicerealms_subscribed';
    if (!localStorage.getItem(SUBSCRIBE_KEY)) {
      ctx.sprites.hide();
      const subLayout = frameRenderer.drawFrame({
        title: 'STAY IN TOUCH',
        borderColor: 'frostBlue',
      });

      const subWriter = frameRenderer.createWriter(subLayout.content, effects);
      const offset = subWriter.calculateVerticalOffset(6);
      subWriter.skip(offset);
      subWriter.blankLine();
      subWriter.writeLine('  Want updates on new ChoiceRealms games?', 'frostSilver');
      subWriter.writeLine('  Enter your email (or press Enter to skip):', 'frostSilver');
      subWriter.blankLine();
      subWriter.beginLine();
      subWriter.write('  > ', 'yellow');
      subWriter.endLine();
      ctx.terminal.showCursor();

      const email = await new Promise<string>((resolve) => {
        const prevMode = ctx.input.getMode();
        ctx.input.setMode('text');
        const handler = (event: { type: string; text?: string }) => {
          if (event.type === 'textSubmit') {
            ctx.input.off(handler);
            ctx.input.setMode(prevMode);
            resolve(event.text ?? '');
          } else if (event.type === 'cancel') {
            ctx.input.off(handler);
            ctx.input.setMode(prevMode);
            resolve('');
          }
        };
        ctx.input.on(handler);
      });

      ctx.terminal.hideCursor();

      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        try {
          await fetch('https://api.choicerealms.com/api/subscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email.trim(), source: 'gallowcrest' }),
          });
          subWriter.blankLine();
          subWriter.writeLine('  Check your email to confirm. Thanks!', 'green');
        } catch {
          subWriter.blankLine();
          subWriter.writeLine('  Could not subscribe right now.', 'red');
        }
        localStorage.setItem(SUBSCRIBE_KEY, '1');
        await subWriter.pressAnyKey();
      } else if (email.trim() === '') {
        // Skipped
      } else {
        subWriter.blankLine();
        subWriter.writeLine('  Invalid email, skipping.', 'red');
        await effects.delay(1500);
      }
    }

    // ─── End Menu ────────────────────────────────────

    ctx.sprites.hide();
    const endLayout = frameRenderer.drawFrame({
      title: 'WHAT NEXT?',
      borderColor: 'frostBlue',
    });

    const endWriter = frameRenderer.createWriter(endLayout.content, effects);
    const endOffset = endWriter.calculateVerticalOffset(6);
    endWriter.skip(endOffset);
    endWriter.blankLine();

    const options: MenuOption[] = [
      { label: 'New Game', color: 'green' },
      { label: 'Return to Title', color: 'amber' },
    ];

    const menuCfg = endWriter.menuConfig();
    const sel = await menu.show(options, {
      showExit: false,
      startRow: menuCfg.startRow,
      startCol: menuCfg.startCol,
      maxWidth: menuCfg.maxWidth,
    });

    switch (sel) {
      case 0:
        ctx.gameState = null;
        await engine.replaceScene(new CharCreateScene());
        return;
      case 1:
      default:
        ctx.gameState = null;
        await engine.replaceScene(new TitleScene());
        return;
    }
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }
}
